<!---区域设备组件-->
<template>
  <div class="area-equipment-box">
    <ScreenTitle name="区域设备导航" />
    <el-input
      v-model="name"
      style="margin-top: 10px; margin-bottom: 10px"
      placeholder="请输入名称搜索"
      clearable
      @clear="clear"
    >
      <template #suffix>
        <el-icon class="el-input__icon" @click="getList">
          <Search />
        </el-icon>
      </template>
    </el-input>
    <div class="tree-container">
      <el-tree
        ref="treeRef"
        default-expand-all
        node-key="id"
        :current-node-key="currentNodeKey"
        :check-strictly="false"
        :show-checkbox="checkbox"
        :props="treeProps"
        :accordion="true"
        :data="list"
        @node-click="handleNodeClick"
        @check="handleCheckChange"
      >
      </el-tree>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  nextTick,
  watch
} from 'vue';
import { getAreaEquipment } from '@/api/model/equipment';

const emits = defineEmits(['change', 'multiChange']),
  { proxy } = getCurrentInstance(),
  props = defineProps({
    //是否允许多选
    checkbox: {
      required: false,
      type: Boolean,
      default: false
    },
    //最多允许选择几个
    maxSelected: {
      required: false,
      type: Number,
      default: 1
    }
  });

// 定义属性
let name = ref(), //根据名称查询
  list = ref([]),
  treeRef = ref(),
  currentNodeKey = ref(null),
  selectedItems = ref([]), // 存储多选时的选中项完整信息
  selectedIds = ref([]); // 存储多选时的选中项ID数组

// 自定义树属性，用于禁用区域节点的复选框
// eslint-disable-next-line one-var
const treeProps = {
  label: 'name',
  children: 'children',
  // 自定义节点渲染，禁用区域节点的复选框
  disabled: (data, node) => {
    // 如果是多选模式，禁用所有有子节点的区域节点
    if (props.checkbox) {
      return data.children && data.children.length > 0;
    }
    return false;
  }
};

// 监听checkbox变化，重置选择状态
watch(
  () => props.checkbox,
  (newVal) => {
    if (newVal) {
      // 切换到多选模式，清空单选状态
      currentNodeKey.value = null;
      if (treeRef.value) {
        treeRef.value.setCurrentKey(null);
      }
    } else {
      // 切换到单选模式，清空多选状态
      selectedItems.value = [];
      selectedIds.value = [];
      if (treeRef.value) {
        treeRef.value.setCheckedKeys([]);
      }
    }
  },
  { immediate: true }
);

/** 查询区域设备列表 */
// eslint-disable-next-line one-var
const getList = () => {
    const params = { name: name.value };

    proxy.$apiLoding.queryLoding(params, getAreaEquipment).then((res) => {
      if (res.success && res.data) {
        list.value = res.data;

        // 使用 nextTick 等待 DOM 更新完成
        nextTick(() => {
          // 根据模式选择默认选中逻辑
          if (props.checkbox) {
            // 多选模式：初始化空数组
            selectedItems.value = [];
            selectedIds.value = [];
            if (treeRef.value) {
              treeRef.value.setCheckedKeys([]);
            }
          } else {
            // 单选模式：查找第一个设备
            // eslint-disable-next-line no-use-before-define
            const firstDevice = findFirstDevice(list.value);

            debugger;
            if (firstDevice) {
              currentNodeKey.value = firstDevice.id;
              if (treeRef.value) {
                treeRef.value.setCurrentKey(firstDevice.id);
              }
              emits('change', firstDevice);
            }
          }
        });
      }
    });
  },
  // 递归查找第一个设备节点
  findFirstDevice = (data) => {
    if (!data || !Array.isArray(data)) {
      return null;
    }

    for (const item of data) {
      // 如果是设备节点（没有children属性或者children为空）
      if (!item.children || item.children.length === 0) {
        return item;
      }

      // 如果是区域节点，递归查找其子节点
      if (item.children && item.children.length > 0) {
        const device = findFirstDevice(item.children);

        if (device) {
          return device;
        }
      }
    }

    return null;
  },
  // 清除监听
  clear = () => {
    name.value = null;
    getList();
  },
  // 点击事件（单选模式）
  handleNodeClick = (data, node) => {
    if (!props.checkbox && node.level === 2) {
      currentNodeKey.value = data.id;
      emits('change', data);
    }
  },
  // 复选框变化事件（多选模式）
  handleCheckChange = (data, checkedState) => {
    if (!props.checkbox) {
      return;
    }

    // 延迟处理，确保DOM更新完成
    setTimeout(() => {
      // 获取所有选中的节点
      const checkedKeys = treeRef.value?.getCheckedKeys() || [],
        checkedNodes = treeRef.value?.getCheckedNodes() || [],
        // 过滤出设备节点（没有子节点的节点）
        deviceNodes = checkedNodes.filter((node) => {
          return !node.children || node.children.length === 0;
        }),
        // 过滤设备节点的ID
        deviceKeys = deviceNodes.map((node) => node.id);

      // 检查是否超过最大限制
      if (deviceKeys.length > props.maxSelected) {
        // 超过限制，需要移除最早选中的项
        const newDeviceNodes = [...deviceNodes],
          newDeviceKeys = [...deviceKeys];

        // 移除超出的项（从最早选中的开始移除）
        while (newDeviceKeys.length > props.maxSelected) {
          newDeviceNodes.shift();
          newDeviceKeys.shift();
        }

        // 更新树的选中状态
        treeRef.value?.setCheckedKeys(newDeviceKeys);

        // 更新存储的数据
        selectedItems.value = newDeviceNodes;
        selectedIds.value = newDeviceKeys;
      } else {
        // 未超过限制，正常更新
        selectedItems.value = deviceNodes;
        selectedIds.value = deviceKeys;
      }

      // 触发多选事件
      emits('multiChange', {
        ids: selectedIds.value,
        items: selectedItems.value
      });
    }, 0);
  },
  // 检查节点是否为设备节点
  isDeviceNode = (node) => {
    return !node.children || node.children.length === 0;
  };

onMounted(() => {
  getList();
});

onUnmounted(() => {
  name.value = null;
  list.value = [];
  currentNodeKey.value = null;
  selectedItems.value = [];
  selectedIds.value = [];
});
</script>

<style lang="scss" scoped>
//设备区域布局
.area-equipment-box {
  width: 16%;
  height: calc(100% - 20px);
  padding: 10px;
  background: white;
  box-shadow: 0 0 0 1px gainsboro; /* 阴影效果 */
  display: flex;
  flex-direction: column;
}

.tree-container {
  flex: 1;
  overflow-y: auto;
  min-height: 0; /* 关键：允许容器缩小 */
  /* 可选：美化滚动条 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f5f5f5;
}

// 自定义选中样式（如果需要）
:deep(.el-tree) {
  .is-current {
    .el-tree-node__content {
      background-color: #ecf5ff !important;
      color: #409eff !important;
      font-weight: 500;
    }
  }

  // 多选模式下高亮选中节点
  .el-checkbox.is-checked + .el-tree-node__content {
    background-color: #f0f9ff !important;
  }

  // 禁用区域节点的复选框样式
  .is-disabled {
    .el-checkbox {
      cursor: not-allowed !important;

      .el-checkbox__inner {
        background-color: #f5f7fa !important;
        border-color: #e4e7ed !important;
        cursor: not-allowed !important;

        &:hover {
          border-color: #e4e7ed !important;
        }
      }

      .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
        background-color: #f5f7fa !important;
        border-color: #e4e7ed !important;

        &::after {
          border-color: #c0c4cc !important;
        }
      }
    }

    .el-tree-node__content {
      cursor: default !important;

      &:hover {
        background-color: transparent !important;
      }
    }
  }
}
</style>
